import { makeEntityScaffold } from '@classpass/saga-tools';
import * as logger from 'universal-logger';
import SCHEDULE_CONTEXT from 'constants/schedule-context';
import fetch from '../../../universal-fetch';

export const userLogout = makeEntityScaffold('SAGA.FETCH.LOGOUT_USER', client =>
  (client.fetch || fetch)('/logout', {
    method: 'POST',
    headers: { 'Content-type': 'application/json' },
  })
);

export const userProfile = makeEntityScaffold(
  'SAGA.API.USER_PROFILE',
  (client, { forceReadFromMaster = false } = {}) => {
    if (client.isUsingUnauthenticatedToken()) {
      if (__LOCAL_DEV__) {
        throw new Error('Refusing to make request for user profile endpoint with admin token.');
      } else {
        logger.error('Returning null for request of user profile endpoint with admin token.'); // eslint-disable-line no-console
        return Promise.resolve(null);
      }
    }

    return client.users.activeUserProfile(
      { fields: 'profile_image_url' },
      {
        headers: forceReadFromMaster ? { 'with-master': 1 } : {},
      }
    );
  }
);

export const userVerifyEmail = makeEntityScaffold('SAGA.API.USER_VERIFY_EMAIL', (client, email) =>
  client.users.verifyEmail({
    email,
  })
);

export const userPermissions = makeEntityScaffold('SAGA.API.USER_PERMISSIONS', (client, userId) =>
  client.users.viewPermissions(userId)
);

export const userInactivePermissions = makeEntityScaffold('SAGA.API.USER_INACTIVE_PERMISSIONS', (client, userId) =>
  client.users.inactivePermissions(userId)
);

export const userCancelReasons = makeEntityScaffold('SAGA.API.USERS.CANCEL_REASONS', client =>
  client.users.cancelReasons().then(data => data)
);

export const userCancelMembership = makeEntityScaffold(
  'SAGA.API.USER_UPDATE_MEMBERSHIP',
  (client, userId, npsScore, cancelComment) =>
    client.users.updateMembership(userId, {
      action: 'cancel',
      nps_score: npsScore,
      ...(cancelComment && { comment: cancelComment }),
    })
);

// Past classes over entire lifetime are not included in the userProfile
export const userPastReservationsCount = makeEntityScaffold('SAGA.API.USER_PAST_RESERVATIONS_COUNT', (client, userId) =>
  client.users.viewFutureReservations(userId, { past: true, meta_only: true }).then(data => data.meta)
);

export const userPastReservations = makeEntityScaffold(
  'SAGA.API.USER_PAST_RESERVATIONS_RESERVATIONS',
  (client, userId) => client.users.viewReservationsV2(userId, { past: true }).then(data => data.reservations)
);

export const userUpcomingReservations = makeEntityScaffold('SAGA.API.USER_UPCOMING_RESERVATIONS', (client, userId) =>
  client.users.viewReservationsV2(userId).then(data => data.reservations)
);

export const userVideoReservations = makeEntityScaffold('SAGA.API.USER_VIDEO_RESERVATIONS', (client, userId) =>
  client.users.viewVideoReservations(userId)
);

export const studioBookmarks = makeEntityScaffold('SAGA.API.STUDIO_BOOKMARKS', (client, userId) =>
  client.users.getBookmarks(userId).then(data => data.bookmarks)
);

export const userCreditBalance = makeEntityScaffold(
  'SAGA.API.USER_CREDIT_BALANCE',
  (client, userId, options = {}, isNextCycle = false) =>
    client.lifecycle.getCreditBalance(userId, options, isNextCycle).then(data => data.data.credit_balance),
  {
    defaultKey: (client, userId, options, isNextCycle) =>
      isNextCycle ? SCHEDULE_CONTEXT.NEXT_CYCLE : SCHEDULE_CONTEXT.CURRENT_CYCLE,
  }
);

export const userCreditBalanceBreakdown = makeEntityScaffold(
  'SAGA.API.USER_CREDIT_BALANCE_BREAKDOWN',
  (client, userId) =>
    client.lifecycle.getCreditBalanceBreakdown(userId).then(data =>
      data.data.sources.reduce((sources, source) => {
        sources[source.credit_source] = { ...source };
        return sources;
      }, {})
    )
);

export const userSignUpEmail = makeEntityScaffold('SAGA.API.EMAIL_SIGNUP', (client, userData, signUpData) => {
  const { firstName, lastName, email, password, newsletterSubscribed } = userData;
  const { conversion, planId, promotionId, offerId } = signUpData;

  return client.users.create({
    user: {
      first_name: firstName,
      last_name: lastName,
      email,
      password,
    },
    newsletter_subscribed: newsletterSubscribed,
    ...(conversion && Object.keys(conversion).length && { conversion }),
    ...(planId && { plan_id: planId }),
    ...(promotionId && { promotion_id: promotionId }),
    ...(offerId && { offer_id: offerId }),
  });
});

export const userSignUpFacebook = makeEntityScaffold('SAGA.API.FACEBOOK_SIGNUP', (client, token, signUpData) => {
  const { conversion, planId, promotionId, offerId } = signUpData;

  return client.users.create({
    facebook: { token },
    ...(conversion && Object.keys(conversion).length && { conversion }),
    ...(planId && { plan_id: planId }),
    ...(promotionId && { promotion_id: promotionId }),
    ...(offerId && { offer_id: offerId }),
  });
});

export const userAnnouncements = makeEntityScaffold('SAGA.API.USER_ANNOUNCEMENTS', client =>
  client.users.announcements()
);

export const userAccountSummary = makeEntityScaffold('SAGA.API.USER_ACCOUNT_SUMMARY', client =>
  client.users.accountSummary()
);
